import React from "react";
import { isEmpty, get } from "lodash";
import AddressForm from "../AddressForm";
import ObjectForm from "./ObjectForm";
import { convertDynamicFields } from "helpers";
import { phoneValidation, emailValidation } from "util/form";
import { StreetModes } from "constants/index";

const loggedErrors = {};

// different structure with ordering DynamicForm, reuse later once CreateNewCustomer modal is unified
export const getMergedStaticBlocks = (t, staticConfigData, uiBlocksData, formValues, clientType) => {
    if (!staticConfigData || staticConfigData.hidden) {
        return [];
    }

    // const uiConfiguredFields =
    //     typeof uiBlocksData === "function" ? uiBlocksData(t, formValues, clientType) : uiBlocksData;

    const uiConfiguredFields = uiBlocksData;

    if (!uiConfiguredFields || isEmpty(uiConfiguredFields)) {
        return [];
    }

    const mergedStaticBlocks = uiConfiguredFields
        .map(uiField => {
            const fieldName = uiField.key;
            const externalField = staticConfigData.attrs[fieldName] || {};
            // return null for empty field
            if (!uiField.withoutExternalConfig && (!externalField || isEmpty(externalField))) {
                const logKey = `${fieldName}_${staticConfigData.name}_${staticConfigData.description}`;
                if (!loggedErrors[logKey]) {
                    console.info(
                        `⚠️ No external static config found: ${fieldName} | ${staticConfigData.name} | ${staticConfigData.description}`
                    );
                    loggedErrors[`${fieldName}_${staticConfigData.name}_${staticConfigData.description}`] = true;
                }
                // console.warn(
                //     `%c ⚠️ UI cannot find match for static config\nFIELD: ${fieldName}\nBLOCK NAME: ${staticConfigData.name}\nBLOCK DESCRIPTION: ${staticConfigData.description}`
                // );
                // externalField.component = () => null;
                // return null;
            }
            return {
                ...uiField, // set default ui field values
                // ...externalField, // override default ui values with externally configured values
                order: externalField.order,
                disabled: externalField.disabled || uiField.disabled,
                isReq: "required" in externalField && !uiField.isHidden ? externalField.required : uiField.isReq,
                isHidden: externalField.hidden || externalField.hidden || uiField.isHidden,
                isHiddenByConfig: externalField.hidden || externalField.hidden,
                hiddenValues: externalField.hiddenValues,
                key: uiField.key, // name should match static field param => by that key value is being saved
                title: t(externalField.name) || t(uiField.title), // label to be displayed over the field input
            };
        })
        // filter hidden items
        .filter(item => !item.hidden)
        // sort by order param
        .sort((a, b) => {
            if (a.order && b.order) {
                return a.order - b.order;
            }
            return 1;
        });

    return mergedStaticBlocks;
};

// blocks config
const getStaticFormName = (partyType, blockKey) => `${partyType}-${blockKey}-static-form`;
const getDynamicFormName = (partyType, blockKey, entity) =>
    entity ? `${partyType}-${blockKey}-dynamic-form-${entity}` : `${partyType}-${blockKey}-dynamic-form`;
const convertDictionaryObject = dictionaryObject =>
    Object.entries(dictionaryObject).map(([prop, value]) => ({
        key: prop,
        value: value,
    }));

const WORK_PHONE_TYPES = ["contactPerson", "organization", "partner", "serviceobject"];

const addContactPersonContactsTypeValue = contactPersonStaticValues => {
    contactPersonStaticValues.contactsType = [];
    contactPersonStaticValues.contactsValue = [];
    contactPersonStaticValues.contactsId = []; // without it receive error "Contacts info cant be null"
    if (contactPersonStaticValues.workPhone) {
        contactPersonStaticValues.contactsType.push("workPhone");
        contactPersonStaticValues.contactsValue.push(contactPersonStaticValues.workPhone);
        contactPersonStaticValues.contactsId.push("");
        delete contactPersonStaticValues.workPhone;
    }

    if (contactPersonStaticValues.homePhone) {
        contactPersonStaticValues.contactsType.push("homePhone");
        contactPersonStaticValues.contactsValue.push(contactPersonStaticValues.homePhone);
        contactPersonStaticValues.contactsId.push("");
        delete contactPersonStaticValues.homePhone;
    }

    if (contactPersonStaticValues.email) {
        contactPersonStaticValues.contactsType.push("email");
        contactPersonStaticValues.contactsValue.push(contactPersonStaticValues.email);
        contactPersonStaticValues.contactsId.push("");
        delete contactPersonStaticValues.email;
    }
};

const getPersonalDataColumn = (t, customerType, modalFormData, incomePhoneNumber, customerId, selectedSearchCustomerByName) => {
    const { id, partyType, customerPartyType } = customerType;
    const key = "personalData";
    const phoneKey = WORK_PHONE_TYPES.includes(partyType) ? "workPhone" : "homePhone";
    let staticWidgets = [
        {
            key: "lastName",
            title: t("persons.lastName"),
            maxLength: 50,
            widgetType: "input",
        },
        {
            key: "firstName",
            title: t("persons.firstName"),
            maxLength: 50,
            isReq: true,
            widgetType: "input",
        },
        {
            key: "patronymic",
            title: t("persons.patronymic"),
            maxLength: 50,
            widgetType: "input",
        },
        {
            key: "issues",
            title: t("persons.issues"),
            values: convertDictionaryObject(t("dictionary:contactIssue", { returnObjects: true })),
            isReq: true,
            widgetType: "multiselect",
        },
        {
            key: phoneKey,
            title: t("persons.phone"),
            maxLength: 19,
            rule: phone => {
                if (phone) {
                    return phoneValidation(phone);
                }
            },
            isReq: true,
            savedValue: incomePhoneNumber,
            widgetType: "input",
        },
        {
            key: "email",
            title: t("persons.email"),
            maxLength: 255,
            rule: email => {
                if (email) {
                    return emailValidation(email);
                }
            },
            widgetType: "input",
        },
    ];

    // filter issues widget from individual type
    if (partyType === "individual") {
        staticWidgets = staticWidgets.filter(widget => widget.key !== "issues");
    }

    const staticConfigData = modalFormData.personalData;
    if (staticConfigData.hidden) {
        return [];
    }
    const mergedWidgetsWithStaticConfig = staticConfigData
        ? getMergedStaticBlocks(t, staticConfigData, staticWidgets)
        : staticWidgets;
    const staticForm = [
        {
            view: "1column",
            title: t(staticConfigData.translationLabel),
            collapsed: false,
            key: "new-customer-personal-data",
            widgets: mergedWidgetsWithStaticConfig,
        },
    ];
    const personalDataBlock = {
        key,
        formKey: getStaticFormName(partyType, key),
        type: "static-form",
        form: staticForm,
    };

    // customerId is provided by single new contact person modal, this modal includes dynamic form
    if (partyType === "contactPerson" && !customerId) {
        if (!selectedSearchCustomerByName || (selectedSearchCustomerByName && !selectedSearchCustomerByName.value)) {
            return [personalDataBlock];
        }

        const [selectedCustomerId, partyType] = selectedSearchCustomerByName.value.split("_");
        // add dynamic form based on selected customerId from searchCustomerByName block
        let entity = "CONTACT_PERSON";
        let entityWithCustomerId = `${entity}_${new Date().getTime()}`;
        // let entityWithCustomerId = `${entity}_${selectedCustomerId}`;
        const dynamicBlock = {
            key,
            formKey: getDynamicFormName(partyType, key, entityWithCustomerId), // we need to provide entity with unique timestamp in order to rerender form on customer change
            type: "dynamic-form",
            entity,
            objectTypeCode: partyType,
            // objectTypeId: id,
            ctxObjectType: "CUSTOMER",
            ctxObjectId: selectedCustomerId,
        };
        return [personalDataBlock, dynamicBlock];
    }

    // add dynamic form
    let entity = "CONTACT_PERSON";
    if (partyType === "individual") {
        entity = "CUSTOMER";
    }
    // if (partyType === "contactPerson") {
    //     entity = "CUSTOMER";
    // }
    const dynamicBlock = {
        key,
        formKey: getDynamicFormName(partyType, key, entity),
        type: "dynamic-form",
        entity,
        // objectTypeCode: customerId ? "serviceobject" : partyType,
        objectTypeCode: customerId ? customerPartyType : partyType,
        objectTypeId: id,
        ctxObjectType: customerId ? "CUSTOMER" : "",
        ctxObjectId: customerId,
    };
    return [personalDataBlock, dynamicBlock];
};

const getJuridicalDataColumn = (t, customerType, modalFormData) => {
    const { id, partyType } = customerType;

    const key = "juridicalData";

    const staticConfigData = modalFormData.juridicalData;
    if (staticConfigData.hidden) {
        return [];
    }
    const staticWidgets = [
        {
            key: "shortName",
            title: t("name"),
            maxLength: 255,
            isReq: true,
            widgetType: "input",
        },
    ];

    const mergedWidgetsWithStaticConfig = staticConfigData
        ? getMergedStaticBlocks(t, staticConfigData, staticWidgets)
        : staticWidgets;

    const staticForm = [
        {
            view: "1column",
            title: t(staticConfigData.translationLabel),
            collapsed: false,
            key: "new-customer-juridical-data",
            widgets: mergedWidgetsWithStaticConfig,
        },
    ];

    const juridicalDataBlock = {
        key,
        formKey: getStaticFormName(partyType, key),
        type: "static-form",
        form: staticForm,
    };
    const entity = "CUSTOMER";
    const dynamicBlock = {
        key,
        formKey: getDynamicFormName(partyType, key, entity),
        type: "dynamic-form",
        entity,
        objectTypeCode: partyType,
        objectTypeId: id,
    };
    return [juridicalDataBlock, dynamicBlock];
};

const getAddressColumn = (t, customerType, modalFormData) => {
    const { partyType } = customerType;
    const key = "addressData";
    const staticConfigData = modalFormData.custAddress;
    if (staticConfigData.hidden) {
        return [];
    }
    const addressDataBlock = {
        key,
        formKey: getStaticFormName(partyType, key),
        type: "component",
        component: (
            <AddressForm
                customFormName={getStaticFormName(partyType, key)}
                // withoutStreetMode
                withoutAddressType
                staticBlockFormKey={customerType.formCode}
            />
        ),
    };
    return [addressDataBlock];
};

const getObjectDataColumn = (t, customerType, modalFormData) => {
    const { id, partyType } = customerType;

    const key = "objectData";

    const staticConfigData = modalFormData.objectData;
    if (staticConfigData.hidden) {
        return [];
    }

    const objectDataBlock = {
        key,
        formKey: getStaticFormName(partyType, key),
        type: "component",
        component: (
            <ObjectForm customFormName={getStaticFormName(partyType, key)} staticBlockFormKey={customerType.formCode} />
        ),
    };
    const entity = "CUSTOMER";
    const dynamicBlock = {
        key,
        formKey: getDynamicFormName(partyType, key, entity),
        type: "dynamic-form",
        entity,
        objectTypeCode: partyType,
        objectTypeId: id,
    };
    return [objectDataBlock, dynamicBlock];
};

const getSearchCustomerByNameColumn = (t, customerType, modalFormData) => {
    const { id, partyType, filterId } = customerType;

    const key = "searchCustomerByName";

    const staticConfigData = modalFormData.searchCustomerByName;
    const staticWidgets = [
        {
            key: "customer",
            title: t("name"),
            searchable: true,
            values: [],
            isReq: true,
            customAjaxKey: "customers_preset",
            customAjaxMethod: "post",
            customAjaxParams: query => ({
                data: { page: 1, limit: 10, start: 0, filterId, type: "customer", query },
            }),
            hasExValue: true,
            customAjaxOptionsConverter: result => {
                return result.map(item => {
                    // return { key: item.id, value: item.objectName };
                    // we need to provide both partyType and id
                    // id is used on new contact person submit
                    // partyType is used to fetch correct dynamic form
                    return { ...item, key: `${item.id}_${item.partyType}`, value: item.name };
                });
            },
            widgetType: "ajaxCombo",
        },
    ];

    const mergedWidgetsWithStaticConfig = staticConfigData
        ? getMergedStaticBlocks(t, staticConfigData, staticWidgets)
        : staticWidgets;

    const staticForm = [
        {
            view: "1column",
            title: t(staticConfigData.translationLabel),
            collapsed: false,
            key: "new-customer-search-customer-by-name-data",
            widgets: mergedWidgetsWithStaticConfig,
        },
    ];

    const searchCustomerByNameBlock = {
        key,
        formKey: getStaticFormName(partyType, key),
        type: "static-form",
        form: staticForm,
    };
    return [searchCustomerByNameBlock];
};

export const getFormColumns = (t, customerType, modalFormData, incomePhoneNumber, customerId, selectedSearchCustomerByName) => {
    const { partyType } = customerType;
    if (partyType === "individual") {
        return [
            getPersonalDataColumn(t, customerType, modalFormData, incomePhoneNumber),
            getAddressColumn(t, customerType, modalFormData),
        ];
    }

    if (partyType === "serviceobject") {
        return [
            getObjectDataColumn(t, customerType, modalFormData),
            getPersonalDataColumn(t, customerType, modalFormData, incomePhoneNumber),
        ];
    }

    if (partyType === "contactPerson") {
        // customerId is provided by single new contact person modal, this modal excludes customer search
        if (customerId) {
            return [getPersonalDataColumn(t, customerType, modalFormData, incomePhoneNumber, customerId)];
        }
        return [
            getSearchCustomerByNameColumn(t, customerType, modalFormData),
            getPersonalDataColumn(t, customerType, modalFormData, incomePhoneNumber, customerId, selectedSearchCustomerByName),
        ];
    }

    // by default return params as for partyType === "organization"

    // if (partyType === "organization") {
    return [
        getPersonalDataColumn(t, customerType, modalFormData, incomePhoneNumber),
        getJuridicalDataColumn(t, customerType, modalFormData),
        getAddressColumn(t, customerType, modalFormData),
    ];
    // }
};

const getParsedAddressStaticValues = (formValues, formValuesKeys) => {
    const addressKeys = formValuesKeys.filter(key => key.includes("addressData-static-form"));
    let staticValues = {};
    addressKeys.forEach(key => {
        staticValues = { ...staticValues, ...formValues[key] };
    });
    const streetMode = staticValues.streetMode;
    delete staticValues["streetMode"];
    delete staticValues["_validationTriggerHack"];
    const values = {};
    Object.keys(staticValues).forEach(key => {
        const val = staticValues[key];
        if (val && typeof val === "object" && "label" in val) {
            values[key] = val.label;
        } else {
            values[key] = val;
        }
    });

    // get proper values of building and zipCode
    const buildingNumberValue = get(staticValues, "buildingNumber");
    let buildingNumber =
        buildingNumberValue && typeof buildingNumberValue === "object"
            ? buildingNumberValue.label
            : buildingNumberValue;
    const buildingNumberCreatable = get(staticValues, "buildingNumberCreatableInput");
    if (buildingNumberCreatable) {
        buildingNumber = buildingNumberCreatable;
    }
    if (buildingNumber) {
        values.buildingNumber = buildingNumber || "";
    }

    const zipCodeValue = get(staticValues, "zipCode");
    let zipCode = zipCodeValue && typeof zipCodeValue === "object" ? zipCodeValue.label : zipCodeValue;
    const zipCodeCreatable = get(staticValues, "zipCodeCreatableInput");
    if (zipCodeCreatable) {
        zipCode = zipCodeCreatable;
    }

    if (zipCode) {
        values.zipCode = zipCode || "";
    }

    // we need to clearup selected street value if street mode in manual mode (creating new street)
    if (streetMode === StreetModes.MANUAL) {
        delete values.street;
    } else {
        delete values.streetTypeId;
        delete values.name;
    }

    if (!isEmpty(values)) {
        values.addressType = "physical";
        values.parentId = get(values, "settlement");
        if (get(values, "street")) {
            values.streetId = get(values, "street");
        }
    }

    return values;
};

export const getCustomerCreateParamsData = (customerType, values, customerId) => {
    const { partyType } = customerType;
    // individual
    if (partyType === "individual") {
        const valuesKeys = Object.keys(values);

        const contactPersonStaticKeys = valuesKeys.filter(key => key.includes("personalData-static-form"));
        const contactPersonDynamicKeys = valuesKeys.filter(key => key.includes("personalData-dynamic-form"));
        let contactPersonStaticValues = {};
        contactPersonStaticKeys.forEach(key => {
            contactPersonStaticValues = { ...contactPersonStaticValues, ...values[key] };
        });
        addContactPersonContactsTypeValue(contactPersonStaticValues);

        let contactPersonDynamicValues = {};
        contactPersonDynamicKeys.forEach(key => {
            contactPersonDynamicValues = { ...contactPersonDynamicValues, ...values[key] };
        });

        const party = {
            ...contactPersonStaticValues,
            dynParams: convertDynamicFields(contactPersonDynamicValues),
        };

        const paramsData = {
            party,
        };

        const addressStaticValues = getParsedAddressStaticValues(values, valuesKeys);
        if (!isEmpty(addressStaticValues)) {
            paramsData.address = [addressStaticValues];
        }

        return paramsData;
    }

    // serviceobject
    if (partyType === "serviceobject") {
        const valuesKeys = Object.keys(values);

        const contactPersonStaticKeys = valuesKeys.filter(key => key.includes("personalData-static-form"));
        const contactPersonDynamicKeys = valuesKeys.filter(key => key.includes("personalData-dynamic-form"));
        let contactPersonStaticValues = {};
        contactPersonStaticKeys.forEach(key => {
            contactPersonStaticValues = { ...contactPersonStaticValues, ...values[key] };
        });
        contactPersonStaticValues.contacts = [];
        contactPersonStaticValues.contactsId = []; // without it receive error "Contacts info cant be null"
        if (contactPersonStaticValues.workPhone) {
            contactPersonStaticValues.contacts.push({
                type: "workPhone",
                val: contactPersonStaticValues.workPhone,
            });
            contactPersonStaticValues.contactsId.push("");
            delete contactPersonStaticValues.workPhone;
        }
        if (contactPersonStaticValues.email) {
            contactPersonStaticValues.contacts.push({
                type: "email",
                val: contactPersonStaticValues.email,
            });
            contactPersonStaticValues.contactsId.push("");
            delete contactPersonStaticValues.email;
        }

        let contactPersonDynamicValues = {};
        contactPersonDynamicKeys.forEach(key => {
            contactPersonDynamicValues = { ...contactPersonDynamicValues, ...values[key] };
        });

        const contactPerson = {
            ...contactPersonStaticValues,
            dynParams: convertDynamicFields(contactPersonDynamicValues),
        };

        const partyStaticKeys = valuesKeys.filter(key => key.includes("objectData-static-form"));
        const partyDynamicKeys = valuesKeys.filter(key => key.includes("objectData-dynamic-form"));
        let partyStaticValues = {};
        partyStaticKeys.forEach(key => {
            partyStaticValues = { ...partyStaticValues, ...values[key] };
        });
        let partyDynamicValues = {};
        partyDynamicKeys.forEach(key => {
            partyDynamicValues = { ...partyDynamicValues, ...values[key] };
        });

        const partyData = {
            shortName: get(partyStaticValues, "object.label"),
            params: {
                attr_0: {
                    key: get(partyStaticValues, "apartmentCreatableInput") || get(partyStaticValues, "apartment.value"),
                    val: get(partyStaticValues, "apartmentCreatableInput") || get(partyStaticValues, "apartment.label"),
                },
                attr_1: {
                    key: get(partyStaticValues, "object.value"),
                    val: get(partyStaticValues, "object.label"),
                },
            },
            ext_uid: get(partyStaticValues, "object.value"),
        };

        const party = {
            // ...partyStaticValues,
            ...partyData,
            dynParams: convertDynamicFields(partyDynamicValues),
        };

        const paramsData = {
            contactPerson,
            party,
        };

        return paramsData;
    }

    if (partyType === "contactPerson") {
        // old code
        // const initialBody = {
        //     lastName: "",
        //     firstName: "",
        //     patronymic: "",
        //     lang_code: "NA",
        //     validFrom: moment(),
        //     contactsType: [],
        //     contactsValue: [],
        //     // issues: ['undefined'],
        //     issues: [],
        //     personId: "",
        //     linkId: "",
        //     description: "",
        //     contactsId: [],
        //     customerId: "",
        // };
        const valuesKeys = Object.keys(values);

        const contactPersonStaticKeys = valuesKeys.filter(key => key.includes("personalData-static-form"));
        const contactPersonDynamicKeys = valuesKeys.filter(key => key.includes("personalData-dynamic-form"));
        let contactPersonStaticValues = {};
        contactPersonStaticKeys.forEach(key => {
            contactPersonStaticValues = { ...contactPersonStaticValues, ...values[key] };
        });
        addContactPersonContactsTypeValue(contactPersonStaticValues);

        let contactPersonDynamicValues = {};
        contactPersonDynamicKeys.forEach(key => {
            contactPersonDynamicValues = { ...contactPersonDynamicValues, ...values[key] };
        });

        let customerIdValues = {};
        // customerId is provided by single new contact person modal
        if (customerId) {
            // customerId was provided
            customerIdValues = { customerId };
        } else {
            // customerId was selected
            const customerIdKeys = valuesKeys.filter(key => key.includes("searchCustomerByName-static-form"));
            customerIdKeys.forEach(key => {
                customerIdValues = { ...customerIdValues, ...values[key] };
            });

            // rename customerId param
            if (customerIdValues.customer && customerIdValues.customer.value) {
                const [selectedCustomerId] = customerIdValues.customer.value.split("_");
                customerIdValues.customerId = selectedCustomerId;
                delete customerIdValues.customer;
            }
        }

        const paramsData = {
            ...contactPersonStaticValues,
            ...customerIdValues,
            dynParams: convertDynamicFields(contactPersonDynamicValues),
        };

        return paramsData;
    }

    // by default return params as for partyType === "organization"
    // if (partyType === "organization") {
    const valuesKeys = Object.keys(values);

    const contactPersonStaticKeys = valuesKeys.filter(key => key.includes("personalData-static-form"));
    const contactPersonDynamicKeys = valuesKeys.filter(key => key.includes("personalData-dynamic-form"));
    let contactPersonStaticValues = {};
    contactPersonStaticKeys.forEach(key => {
        contactPersonStaticValues = { ...contactPersonStaticValues, ...values[key] };
    });
    addContactPersonContactsTypeValue(contactPersonStaticValues);

    let contactPersonDynamicValues = {};
    contactPersonDynamicKeys.forEach(key => {
        contactPersonDynamicValues = { ...contactPersonDynamicValues, ...values[key] };
    });

    const contactPerson = {
        ...contactPersonStaticValues,
        dynParams: convertDynamicFields(contactPersonDynamicValues),
    };

    const partyStaticKeys = valuesKeys.filter(key => key.includes("juridicalData-static-form"));
    const partyDynamicKeys = valuesKeys.filter(key => key.includes("juridicalData-dynamic-form"));
    let partyStaticValues = {};
    partyStaticKeys.forEach(key => {
        partyStaticValues = { ...partyStaticValues, ...values[key] };
    });
    let partyDynamicValues = {};
    partyDynamicKeys.forEach(key => {
        partyDynamicValues = { ...partyDynamicValues, ...values[key] };
    });

    const party = {
        ...partyStaticValues,
        dynParams: convertDynamicFields(partyDynamicValues),
    };

    const paramsData = {
        contactPerson,
        party,
    };
    const addressStaticValues = getParsedAddressStaticValues(values, valuesKeys);
    if (!isEmpty(addressStaticValues)) {
        paramsData.address = [addressStaticValues];
    }

    return paramsData;
    // }
};
